import { Badge } from "@devhop/ui";
import {
  AlertTriangleIcon,
  CheckCircle2Icon,
  MessageSquareIcon,
  XCircleIcon,
} from "lucide-react";
import { labelServiceStage } from "../lib/trainingLaoLabels";

export type TrainingServiceStandardDetailModel = {
  id: string;
  name: string;
  description: string | null;
  serviceStage: string;
  status: string;
  updatedAt: Date | string;
  serviceChannelName: string | null;
  departmentName: string | null;
  positionTitle: string | null;
  items: Array<{
    id: string;
    itemType: "do" | "dont";
    content: string;
    description: string | null;
  }>;
  examples: Array<{
    id: string;
    situation: string;
    example: string;
    description: string | null;
  }>;
  specialCases: Array<{
    id: string;
    title: string;
    handling: string;
    description: string | null;
  }>;
};

type Props = {
  standard: TrainingServiceStandardDetailModel;
};

const statusLabels: Record<string, string> = {
  active: "ໃຊ້ງານ",
  draft: "ປັບປຸງ",
  inactive: "ເລີກໃຊ້",
};

const statusColors: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  active: "default",
  draft: "secondary",
  inactive: "destructive",
};

export function TrainingServiceStandardDetailView({ standard }: Props) {
  const doItems = standard.items?.filter((item) => item.itemType === "do") ?? [];
  const dontItems = standard.items?.filter((item) => item.itemType === "dont") ?? [];

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-tourid="training-standard-detail-content">
      <section className="space-y-4 rounded-lg border bg-card p-6">
        <h2 className="font-bold text-xl">ຂໍ້ມູນພື້ນຖານ</h2>

        <div className="space-y-3">
          <div>
            <h3 className="mb-1 font-medium text-muted-foreground text-sm">ຊື່ມາດຕະຖານ</h3>
            <p className="font-semibold text-lg">{standard.name}</p>
          </div>

          {standard.description ? (
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">ຄຳອະທິບາຍ</h3>
              <p className="text-sm">{standard.description}</p>
            </div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">ຊ່ອງທາງບໍລິການ</h3>
              <Badge variant="outline" className="mt-1">
                {standard.serviceChannelName || "-"}
              </Badge>
            </div>

            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">ຕໍາແໜ່ງ</h3>
              <p className="text-sm">
                {standard.positionTitle || "ທຸກຕໍາແໜ່ງໃນແຜນກ"}
              </p>
            </div>

            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">ແຜນກ</h3>
              <p className="text-sm">{standard.departmentName || "-"}</p>
            </div>

            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">ໃຊ້ເວລາໃດ</h3>
              <Badge variant="secondary" className="mt-1">
                {labelServiceStage(standard.serviceStage)}
              </Badge>
            </div>
          </div>

          <div>
            <h3 className="mb-1 font-medium text-muted-foreground text-sm">ສະຖານະ</h3>
            <Badge variant={statusColors[standard.status] ?? "secondary"} className="mt-1">
              {statusLabels[standard.status] ?? standard.status}
            </Badge>
          </div>
        </div>
      </section>

      <section className="space-y-4 rounded-lg border bg-card p-6">
        <div className="flex items-center gap-2">
          <CheckCircle2Icon className="h-5 w-5 text-green-600" />
          <h2 className="font-bold text-xl">ສິ່ງທີ່ຕ້ອງເຮັດ</h2>
        </div>

        {doItems.length > 0 ? (
          <ul className="space-y-3">
            {doItems.map((item, index) => (
              <li key={item.id} className="flex items-start gap-3">
                <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-100 font-medium text-green-700 text-sm">
                  {index + 1}
                </span>
                <div className="flex-1">
                  <p className="font-medium">{item.content}</p>
                  {item.description ? (
                    <p className="mt-1 text-muted-foreground text-sm">{item.description}</p>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນ</p>
        )}
      </section>

      <section className="space-y-4 rounded-lg border bg-card p-6">
        <div className="flex items-center gap-2">
          <XCircleIcon className="h-5 w-5 text-red-600" />
          <h2 className="font-bold text-xl">ສິ່ງທີ່ຫ້າມເຮັດ</h2>
        </div>

        {dontItems.length > 0 ? (
          <ul className="space-y-3">
            {dontItems.map((item, index) => (
              <li key={item.id} className="flex items-start gap-3">
                <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-100 font-medium text-red-700 text-sm">
                  {index + 1}
                </span>
                <div className="flex-1">
                  <p className="font-medium">{item.content}</p>
                  {item.description ? (
                    <p className="mt-1 text-muted-foreground text-sm">{item.description}</p>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນ</p>
        )}
      </section>

      <section className="space-y-4 rounded-lg border bg-card p-6">
        <div className="flex items-center gap-2">
          <MessageSquareIcon className="h-5 w-5 text-blue-600" />
          <h2 className="font-bold text-xl">ຕົວຢ່າງປະໂຫຍກ</h2>
        </div>

        {standard.examples.length > 0 ? (
          <div className="space-y-4">
            {standard.examples.map((ex) => (
              <div
                key={ex.id}
                className="rounded-lg border-blue-500 border-l-4 bg-blue-50 p-4"
              >
                <h3 className="mb-2 font-semibold text-blue-900 text-sm">{ex.situation}</h3>
                <p className="mb-1 text-sm">{ex.example}</p>
                {ex.description ? (
                  <p className="text-muted-foreground text-xs">{ex.description}</p>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນ</p>
        )}
      </section>

      <section className="space-y-4 rounded-lg border bg-card p-6">
        <div className="flex items-center gap-2">
          <AlertTriangleIcon className="h-5 w-5 text-amber-600" />
          <h2 className="font-bold text-xl">ກໍລະນີພິເສດ</h2>
        </div>

        {standard.specialCases.length > 0 ? (
          <div className="space-y-4">
            {standard.specialCases.map((sc) => (
              <div
                key={sc.id}
                className="rounded-lg border-amber-500 border-l-4 bg-amber-50 p-4"
              >
                <h3 className="mb-2 font-semibold text-amber-900 text-sm">{sc.title}</h3>
                <p className="mb-1 text-sm">{sc.handling}</p>
                {sc.description ? (
                  <p className="text-muted-foreground text-xs">{sc.description}</p>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນ</p>
        )}
      </section>
    </div>
  );
}
